import React, { useRef } from 'react'
import InfiniteScroll from 'react-infinite-scroll-component'

import { SelectModal } from '@components/modals/SelectModal'
import { Banner, type BannerProps, Loader, useModalContext } from '@fourthwall/components'

import * as S from './SelectItems.styled'
import type { InputProps, SelectItems } from './SelectItems.types'
import { SelectItem } from './components/SelectItem/SelectItem'
import { useLoadMoreWhenNotScrollable } from './hooks/useLoadMoreWhenNotScrollable'
import { getSelectItemInitialValues } from './utils/getSelectItemInitialValues'
import { getSelectModalMessage } from './utils/getSelectModalMessage'
import { isOfferItemType } from './utils/isOfferItemType'

export type SelectItemsModalViewProps = {
  inputProps: InputProps
  banner?: BannerProps
  selectItems: SelectItems
  submitDisabled?: boolean
  onSubmit: () => void
}

export const SelectItemsModalView = ({
  inputProps,
  banner,
  selectItems,
  submitDisabled,
  onSubmit,
}: SelectItemsModalViewProps) => {
  const { close } = useModalContext()
  const scrollableContentRef = useRef<HTMLDivElement>(null)
  useLoadMoreWhenNotScrollable({
    itemsLength: selectItems.items.length,
    isLoading: selectItems.isLoading,
    hasNextPage: selectItems.hasNextPage,
    scrollableContentRef,
    loadMore: selectItems.onLoadMore,
  })

  const renderBanner = () => {
    if (!banner) {
      return null
    }

    return (
      <S.BannerWrapper>
        <Banner {...banner} />
      </S.BannerWrapper>
    )
  }

  const renderModalContent = () => {
    if (selectItems.isLoading) {
      return (
        <S.IdleContainer>
          <Loader size="large" />
        </S.IdleContainer>
      )
    }

    if (selectItems.emptyMessage) {
      return <S.IdleContainer>{selectItems.emptyMessage}</S.IdleContainer>
    }

    return (
      <InfiniteScroll
        hasMore={selectItems.hasNextPage}
        loader={
          <S.LoaderContainer>
            <Loader />
          </S.LoaderContainer>
        }
        dataLength={selectItems.offersCount}
        scrollableTarget="selectModalContent"
        next={selectItems.onLoadMore}
      >
        <S.SelectItemsList>
          {selectItems.items.map((item) => (
            <SelectItem
              key={isOfferItemType(item) ? item.offerId : item.variantId}
              item={item}
              initialValues={getSelectItemInitialValues(item, selectItems.selectedItems)}
              onChange={selectItems.onItemChange}
            />
          ))}
        </S.SelectItemsList>
      </InfiniteScroll>
    )
  }

  return (
    <SelectModal
      title="Select items for samples order"
      content={
        <S.ContentContainer>
          {renderBanner()}
          {renderModalContent()}
        </S.ContentContainer>
      }
      contentRef={scrollableContentRef}
      message={selectItems.hasErrors ? undefined : getSelectModalMessage(selectItems.selectedItems)}
      submitButtonLabel="Select items"
      submitDisabled={submitDisabled}
      inputProps={inputProps}
      close={close}
      onSubmitClick={onSubmit}
    />
  )
}
